export enum Equipment {
  Bodyweight = 'Bodyweight',
  Dumbbells = 'Dumbbells',
  AerobicStep = 'Aerobic Step',
  SandBags = 'Sandbags',
  SlamBalls = 'Slam Balls',
  SpeedRope = 'Speed Rope',
  Bench = 'Bench',
  HomeBench = 'Home Bench',
  Pushbells = 'Pushbells',
  ShortUltrabands = 'Short Ultrabands',
  LongUltrabands = 'Long Ultrabands',
  UniversalBands = 'Universal Bands',
  Kettlebells = 'Kettlebells',
  BandsLowFixed = 'Bands Low Fixed',
  BandsHighFixed = 'Bands High Fixed',
}

type UniBandWeights =
  | 'X-LIGHT'
  | 'LIGHT'
  | 'MEDIUM'
  | 'HEAVY'
  | 'X-HEAVY'
  | 'XX-HEAVY';
type MiniBandWeights = 'X-LIGHT' | 'LIGHT' | 'MEDIUM' | 'HEAVY' | 'X-HEAVY';
type DumbbellWeights =
  | '1kg'
  | '2kg'
  | '4kg'
  | '5kg'
  | '6kg'
  | '8kg'
  | '10kg'
  | '12.5kg'
  | '15kg'
  | '20kg'
  | '25kg'
  | '32.5kg'
  | '40kg';
type KettlebellWeights =
  | '2kg'
  | '3kg'
  | '4kg'
  | '6kg'
  | '8kg'
  | '12kg'
  | '16kg'
  | '20kg'
  | '24kg'
  | '28kg'
  | '32kg';

type SandBagWeights = '5kg' | '10kg' | '15kg' | '20kg' | '25kg';

type SlamBallWeights = '5kg' | '10kg' | '15kg' | '20kg' | '25kg' | '30kg' | '40kg';

const benchTypes = [Equipment.Bench, Equipment.AerobicStep, Equipment.HomeBench];
const bandTypes = [Equipment.ShortUltrabands, Equipment.LongUltrabands, Equipment.BandsLowFixed,
   Equipment.BandsHighFixed, Equipment.UniversalBands];

export type EquipmentWeight =
  | UniBandWeights
  | MiniBandWeights
  | DumbbellWeights
  | KettlebellWeights
  | SandBagWeights
  | SlamBallWeights;

export const smallBodyGroupTypes = [
  'Shoulders',
  'Triceps',
  'Chest',
  'Abdomen',
  'Glutes & Hips',
  'Upper Legs',
  'Biceps',
  'Upper Back & Traps',
  'Lower Back & Lats',
] as const;

export const smallBodyGroupTypesArray = smallBodyGroupTypes.map((type: string) => type as SmallBodyGroupType);

type SmallBodyGroupTuple = typeof smallBodyGroupTypes;
export type SmallBodyGroupType = SmallBodyGroupTuple[number];

export type StrengthRatioForMuscleGroups = {
  [key in SmallBodyGroupType]: number;
};

export enum BenchPosition {
  HomeStep = 'Home Step',
  FlatBench = 'Flat Bench',
  InclineBench = 'Incline Bench',
  VerticalBench = 'Vertical Bench',
  DeclineBench = 'Decline Bench',
  SemiInclineBench = 'Semi Incline Bench',
}
export enum BenchHub {
  Flat = 'Flat',
  Low = 'Low',
  High = 'High',
}

export enum BenchAnchorPoint {
  NONE = 'No anchor point',
  BANDS_1 = 'Bands 1',
  BANDS_2 = 'Bands 2',
  BANDS_3 = 'Bands 3',
  BANDS_4 = 'Bands 4',
  BANDS_5 = 'Bands 5',
  BANDS_6 = 'Bands 6',
  BANDS_7 = 'Bands 7',
}

export enum EquipmentCombinations {
  BENCH_BANDS = 'Bench, Bands',
  BENCH_DUMBBELLS = 'Bench, Dumbbells',
  BENCH_BANDS_DUMBBELLS = 'Bench, Bands, Dumbbells',
  BENCH_BANDS_ANCHOR_POINT = 'Bench, Bands, Anchor point',
  BANDS_ANCHOR_POINT = 'Bands, Anchor point',
  BODYWEIGHT = 'No Equipment',
  BODYWEIGHT_LIGHT = 'Bodyweight',
  DUMBBELLS = 'Dumbbells',
  BANDS = 'Bands',
  STUDIO_BANDS = 'Studio Bands',
  BENCH = 'Bench',
  BENCH_HUB_BANDS = 'BenchHub Bands',
  BENCH_HUB = 'BenchHub',
  BENCH_HUB_DUMBBELLS = 'BenchHub, Dumbbells',
  BENCH_HUB_BANDS_ANCHOR_POINT = 'BenchHub, Bands, Anchor point',
}

export enum BenchOption {
  Flat = 'Flat',
  Opt1 = 'Opt 1',
  Opt2 = 'Opt 2',
  Opt3 = 'Opt 3',
  Opt4 = 'Opt 4',
  Opt5 = 'Opt 5',
  Opt6 = 'Opt 6',
  Opt7 = 'Opt 7',
  Opt8 = 'Opt 8',
  Opt9 = 'Opt 9',
  Opt10 = 'Opt 10',
  Opt11 = 'Opt 11',
  Opt12 = 'Opt 12',
  Opt13 = 'Opt 13',
  Opt14 = 'Opt 14',
  Opt15 = 'Opt 15',
  Opt16 = 'Opt 16',
  Opt17 = 'Opt 17',
  Opt18 = 'Opt 18',
  Opt19 = 'Opt 19',
  Opt20 = 'Opt 20',
}

export type EquipmentRequiringInstruction =
  | Equipment.ShortUltrabands
  | Equipment.UniversalBands
  | Equipment.Dumbbells
  | Equipment.Pushbells
  | Equipment.SandBags
  | Equipment.SlamBalls
  | Equipment.Kettlebells
  | Equipment.LongUltrabands
  | Equipment.BandsLowFixed
  | Equipment.BandsHighFixed;

export const equipmentRequiringInstruction: EquipmentRequiringInstruction[] = [
  Equipment.ShortUltrabands,
  Equipment.UniversalBands,
  Equipment.Dumbbells,
  Equipment.Pushbells,
  Equipment.SandBags,
  Equipment.SlamBalls,
  Equipment.Kettlebells,
  Equipment.BandsLowFixed,
  Equipment.BandsHighFixed,
  Equipment.LongUltrabands,
];

export const benchOptionsToBenchPositionsConfig = {
  [BenchOption.Flat]: BenchPosition.FlatBench,
  [BenchOption.Opt1]: BenchPosition.HomeStep,
  [BenchOption.Opt2]: BenchPosition.FlatBench,
  [BenchOption.Opt3]: BenchPosition.FlatBench,
  [BenchOption.Opt4]: BenchPosition.FlatBench,
  [BenchOption.Opt5]: BenchPosition.DeclineBench,
  [BenchOption.Opt6]: BenchPosition.FlatBench,
  [BenchOption.Opt7]: BenchPosition.InclineBench,
  [BenchOption.Opt8]: BenchPosition.FlatBench,
  [BenchOption.Opt9]: BenchPosition.VerticalBench,
  [BenchOption.Opt10]: BenchPosition.HomeStep,
  [BenchOption.Opt11]: BenchPosition.InclineBench,
  [BenchOption.Opt12]: BenchPosition.InclineBench,
  [BenchOption.Opt13]: BenchPosition.InclineBench,
  [BenchOption.Opt14]: BenchPosition.VerticalBench,
  [BenchOption.Opt15]: BenchPosition.VerticalBench,
  [BenchOption.Opt16]: BenchPosition.VerticalBench,
  [BenchOption.Opt17]: BenchPosition.VerticalBench,
  [BenchOption.Opt18]: BenchPosition.VerticalBench,
  [BenchOption.Opt19]: BenchPosition.SemiInclineBench,
  [BenchOption.Opt20]: BenchPosition.HomeStep,
};

export const benchOptionsToAnchorPointsConfig = {
  [BenchOption.Flat]: BenchAnchorPoint.NONE,
  [BenchOption.Opt1]: BenchAnchorPoint.NONE,
  [BenchOption.Opt2]: BenchAnchorPoint.NONE,
  [BenchOption.Opt3]: BenchAnchorPoint.BANDS_1,
  [BenchOption.Opt4]: BenchAnchorPoint.BANDS_2,
  [BenchOption.Opt5]: BenchAnchorPoint.NONE,
  [BenchOption.Opt6]: BenchAnchorPoint.BANDS_3,
  [BenchOption.Opt7]: BenchAnchorPoint.NONE,
  [BenchOption.Opt8]: BenchAnchorPoint.BANDS_7,
  [BenchOption.Opt9]: BenchAnchorPoint.NONE,
  [BenchOption.Opt10]: BenchAnchorPoint.BANDS_6,
  [BenchOption.Opt11]: BenchAnchorPoint.BANDS_5,
  [BenchOption.Opt12]: BenchAnchorPoint.BANDS_6,
  [BenchOption.Opt13]: BenchAnchorPoint.BANDS_7,
  [BenchOption.Opt14]: BenchAnchorPoint.BANDS_1,
  [BenchOption.Opt15]: BenchAnchorPoint.BANDS_2,
  [BenchOption.Opt16]: BenchAnchorPoint.BANDS_3,
  [BenchOption.Opt17]: BenchAnchorPoint.BANDS_4,
  [BenchOption.Opt18]: BenchAnchorPoint.BANDS_5,
  [BenchOption.Opt19]: BenchAnchorPoint.BANDS_7,
  [BenchOption.Opt20]: BenchAnchorPoint.BANDS_5,
};

export const benchBenchPositionsToHubConfig = {
  [BenchPosition.FlatBench]: BenchHub.High,
  [BenchPosition.HomeStep]: BenchHub.Low,
  [BenchPosition.InclineBench]: BenchHub.High,
  [BenchPosition.VerticalBench]: BenchHub.High,
  [BenchPosition.DeclineBench]: BenchHub.High,
  [BenchPosition.SemiInclineBench]: BenchHub.High,
};

export class BaseEquipment {
  public static EquipmentCombinationList: { [key in EquipmentCombinations]?: Equipment[] } = {
    [EquipmentCombinations.BODYWEIGHT]: [Equipment.Bodyweight],
    [EquipmentCombinations.BENCH_BANDS]: [
      Equipment.HomeBench,
      Equipment.UniversalBands, Equipment.LongUltrabands, Equipment.ShortUltrabands,
      Equipment.Bodyweight],
    [EquipmentCombinations.BENCH_DUMBBELLS]: [
      Equipment.HomeBench,
      Equipment.Dumbbells, Equipment.Pushbells,
      Equipment.Bodyweight],
    [EquipmentCombinations.BENCH_BANDS_DUMBBELLS]: [
      Equipment.HomeBench,
      Equipment.UniversalBands, Equipment.LongUltrabands, Equipment.ShortUltrabands,
      Equipment.Dumbbells, Equipment.Pushbells,
      Equipment.Bodyweight],
    [EquipmentCombinations.DUMBBELLS]: [Equipment.Dumbbells, Equipment.Pushbells, Equipment.Bodyweight],
    [EquipmentCombinations.BENCH]: [Equipment.HomeBench],
    [EquipmentCombinations.BANDS]: [Equipment.UniversalBands, Equipment.LongUltrabands, Equipment.ShortUltrabands],
    [EquipmentCombinations.STUDIO_BANDS]: [
      Equipment.LongUltrabands,
      Equipment.ShortUltrabands,
      Equipment.BandsLowFixed,
      Equipment.BandsHighFixed,
    ],
    [EquipmentCombinations.BENCH_HUB_DUMBBELLS]: [
      Equipment.Bench,
      Equipment.Dumbbells, Equipment.Pushbells,
      Equipment.Bodyweight],
  };

  public static isValidAnchorPoint(anchorPoint: BenchAnchorPoint) {
    if (anchorPoint === BenchAnchorPoint.NONE) {
      return false;
    }
    for(const val in BenchAnchorPoint) {
      if (BenchAnchorPoint[val] === anchorPoint) {
        return true;
      }
    }
    return false;
  }

  public static isBodyweightOnly(equipment: Equipment[]): boolean {
    return equipment.length === 1  && equipment[0] === Equipment.Bodyweight;
  }

  public static getDumbbells(): Equipment[] {
    return [
      ...BaseEquipment.EquipmentCombinationList[EquipmentCombinations.DUMBBELLS] as Equipment[],
    ];
  }

  public static getBenchAndDumbbells(): Equipment[] {
    return [
      ...BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BENCH_HUB_DUMBBELLS] as Equipment[],
    ];
  }

  public static getEliteBenchAndBands(): Equipment[] {
    return [...BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BENCH_BANDS] as Equipment[]];
  }

  public static getEliteBenchAndDumbells(): Equipment[] {
    return [...BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BENCH_DUMBBELLS] as Equipment[]];
  }
  public static getEliteBenchAndBandsAndDumbbells(): Equipment[] {
    return [...BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BENCH_BANDS_DUMBBELLS] as Equipment[]];
  }

  public static getBodyWeight(): Equipment[] {
    return [Equipment.Bodyweight];
  }

  public static replaceHomeBenchWithStudioBench(equipment: Equipment[] | undefined): Equipment[] {
    if (!equipment) {
      return [];
    }

    return equipment.map((eq) => {
      if (eq === Equipment.HomeBench) {
        return Equipment.Bench;
      }

      return eq;
    });
  }

  public static containsHomeDumbbells(equipment: Equipment[]) {
    if (equipment == null) {
      return false;
    }
    const dumbbellsEquipmentExists = BaseEquipment.containsDumbbells(equipment);
    return dumbbellsEquipmentExists;
  }

  public static containsDumbbells(equipment: Equipment[]) {
    return BaseEquipment.hasEquipmentPiece(Equipment.Dumbbells, equipment);
  }

  public static containsHomeBench(equipment: Equipment[]) {
    return BaseEquipment.hasEquipmentPiece(Equipment.HomeBench, equipment);
  }

  public static containsBench(equipment: Equipment[]) {
    return BaseEquipment.hasEquipmentPiece(Equipment.Bench, equipment);
  }

  public static containsSandBags(equipment: Equipment[]) {
   return BaseEquipment.hasEquipmentPiece(Equipment.SandBags, equipment);
  }

  public static containsSlamBalls(equipment: Equipment[]) {
    return BaseEquipment.hasEquipmentPiece(Equipment.SlamBalls, equipment);
  }

  public static containsKettlebells(equipment: Equipment[]) {
    return BaseEquipment.hasEquipmentPiece(Equipment.Kettlebells, equipment);
  }

  public static containsHomeBands(equipment: Equipment[]) {
    const bands = BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BANDS] as Equipment[];
    return BaseEquipment.includesEquipmentPieces(bands, equipment);

  }

  public static containsStudioBands(equipment: Equipment[]) {
    const studioBands = this.EquipmentCombinationList[EquipmentCombinations.STUDIO_BANDS];
    return BaseEquipment.includesEquipmentPieces(studioBands!, equipment);
  }

  public static bandsArePrimaryEquipment(equipment: Equipment[]) {
    if(!equipment) {
      return false;
    }
    if (equipment.length === 1) {
      return this.isBand(equipment[0]);
    }
    return equipment.every((item) => this.isBench(item) || this.isBand(item));
  }

  public static onlyContainsBench(equipment: Equipment[]) {
    if (!equipment || equipment.length > 1) {
      return false;
    }
    return this.isBench(equipment[0]);
  }

  public static getBandInEquipmentList(equipment: Equipment[]) {
    return equipment.find((item) => this.isBand(item));
  }

  public static isEliteEquipment(equipment: Equipment[]) {
    return BaseEquipment.containsHomeDumbbells(equipment)
    || BaseEquipment.containsHomeBands(equipment);
  }

  public static containsAllHomeBands(equipment: Equipment[]) {
    if (equipment == null) {
      return false;
    }
    const bands = BaseEquipment.EquipmentCombinationList[EquipmentCombinations.BANDS] as Equipment[];
    const bandsNotFoundInEquipment = bands.find((band) => equipment.indexOf(band) === -1);
    const doesEquipmentContainAllBands = bandsNotFoundInEquipment === undefined;
    return doesEquipmentContainAllBands;
  }

  public static containsBodyWeigth(equipment: Equipment[]) {
    if (equipment == null) {
      return false;
    }
    return equipment.indexOf(Equipment.Bodyweight) !== -1;
  }

  public static containsUniversalBand(equipment: Equipment[]) {
    if (equipment == null) {
      return false;
    }
    return equipment.indexOf(Equipment.UniversalBands) !== -1;
  }

  public static hasEitherBandsOrDumbells(equipmentCombination: EquipmentCombinations) {
    const bands = BaseEquipment.hasBandsCombination(equipmentCombination);
    const dumbbells = BaseEquipment.hasOnlyDumbbells(equipmentCombination);

    return bands || dumbbells;
  }

  public static hasBandsCombination(equipmentCombination: EquipmentCombinations) {
    return equipmentCombination === EquipmentCombinations.BENCH_BANDS_DUMBBELLS ||
      equipmentCombination === EquipmentCombinations.BENCH_BANDS;
  }

  public static hasOnlyDumbbells(equipmentCombination: EquipmentCombinations) {
    return equipmentCombination === EquipmentCombinations.DUMBBELLS;
  }

    public static hasBenchAndDumbbellsCombination(equipmentCombo: EquipmentCombinations) {
    return equipmentCombo === EquipmentCombinations.BENCH_HUB_DUMBBELLS;
  }

  public static getEquipmentCombination(equipment: Equipment[]): EquipmentCombinations {
    if (BaseEquipment.containsHomeBench(equipment)
      && BaseEquipment.containsHomeBands(equipment)
      && BaseEquipment.containsHomeDumbbells(equipment)) {
      return EquipmentCombinations.BENCH_BANDS_DUMBBELLS;
    }
    if (BaseEquipment.containsHomeBench(equipment) && BaseEquipment.containsHomeBands(equipment)) {
      return EquipmentCombinations.BENCH_BANDS;
    }
    if (BaseEquipment.containsBench(equipment) && BaseEquipment.containsHomeDumbbells(equipment)) {
      return EquipmentCombinations.BENCH_HUB_DUMBBELLS;
    }
    if (BaseEquipment.containsHomeBench(equipment) && BaseEquipment.containsHomeDumbbells(equipment)) {
      return EquipmentCombinations.BENCH_DUMBBELLS;
    }
    if (BaseEquipment.containsHomeBench(equipment)) {
      return EquipmentCombinations.BENCH;
    }
    if (BaseEquipment.containsHomeDumbbells(equipment)) {
      return EquipmentCombinations.DUMBBELLS;
    }
    if (BaseEquipment.containsHomeBands(equipment)) {
      return EquipmentCombinations.BANDS;
    }
    if (BaseEquipment.containsBodyWeigth(equipment)) {
      return EquipmentCombinations.BODYWEIGHT;
    }

    // this error crashes (debug?) apps
    throw new Error('invalid equipment ' + equipment);
  }

  public static getEquipmentCombinationForDisplay(equipment: Equipment[]): string {
    const enumValue = BaseEquipment.getEquipmentCombination(equipment);
    return enumValue.replace('BenchHub', 'Bench');
  }

  public static isHomeDumbbellsEquipmentSelection(equipment?: Equipment[]): boolean {
    if (equipment) {
      try {
        return this.getEquipmentCombination(equipment) === EquipmentCombinations.DUMBBELLS;
      } catch { /* no-op */
      }
    }
    return false;
  }

  public static isHomeBenchBandsEquipmentSelection(equipment?: Equipment[]): boolean {
    if (equipment) {
      try {
        return this.getEquipmentCombination(equipment) === EquipmentCombinations.BENCH_BANDS;
      } catch { /* no-op */
      }
    }
    return false;
  }

  public static isBenchDumbbellsEquipmentSelection(equipment?: Equipment[]): boolean {
    if (equipment) {
      try {
        return this.getEquipmentCombination(equipment) === EquipmentCombinations.BENCH_HUB_DUMBBELLS;
      } catch { /* no-op */
      }
    }
    return false;
  }

  public static getEquipmentCombinationForTimingConfigs(equipment: Equipment[],
                                                        currentBenchPosition?: BenchPosition,
                                                        previousBenchPosition?: BenchPosition,
                                                        anchorPoint?: BenchAnchorPoint[] ): EquipmentCombinations {
    const benchChange = BaseEquipment.containsHomeBench(equipment) &&
      this.detectBenchChange(currentBenchPosition, previousBenchPosition);
    const hasBenchChange = !!benchChange;

    if ( hasBenchChange
      && BaseEquipment.containsHomeBands(equipment)
      && BaseEquipment.containsHomeDumbbells(equipment)) {
      return EquipmentCombinations.BENCH_BANDS_DUMBBELLS;
    }

    const hasAnchorPoint = anchorPoint &&
      anchorPoint.length > 0 &&
      BaseEquipment.isValidAnchorPoint(anchorPoint[0]);

    if (hasBenchChange &&
          BaseEquipment.containsHomeBands(equipment) && hasAnchorPoint) {
      return benchChange === EquipmentCombinations.BENCH_HUB ?
        EquipmentCombinations.BENCH_HUB_BANDS_ANCHOR_POINT : EquipmentCombinations.BENCH_BANDS_ANCHOR_POINT;
    }
    if (BaseEquipment.containsHomeBands(equipment) && hasAnchorPoint) {
      return EquipmentCombinations.BANDS_ANCHOR_POINT;
    }
    if (hasBenchChange && BaseEquipment.containsHomeBands(equipment)) {
      return benchChange === EquipmentCombinations.BENCH_HUB ?
        EquipmentCombinations.BENCH_HUB_BANDS : EquipmentCombinations.BENCH_BANDS;
    }
    if (hasBenchChange &&
          BaseEquipment.containsHomeDumbbells(equipment)) {
      return benchChange === EquipmentCombinations.BENCH_HUB ?
        EquipmentCombinations.BENCH_HUB_DUMBBELLS : EquipmentCombinations.BENCH_DUMBBELLS;
    }
    if (hasBenchChange) {
      return benchChange as EquipmentCombinations;
    }
    if (BaseEquipment.containsHomeDumbbells(equipment)) {
      return EquipmentCombinations.DUMBBELLS;
    }
    if (BaseEquipment.containsHomeBands(equipment)) {
      return EquipmentCombinations.BANDS;
    }
    return EquipmentCombinations.BODYWEIGHT;
  }

  public static removeBenchFromEquipment(equipment: Equipment[]): Equipment[] {
    return equipment.filter((equipmentItem: Equipment) => {
      return !BaseEquipment.isHomeBench(equipmentItem);
    });
  }

  public static detectBenchChange(
    currentBenchPosition?: BenchPosition,
    previousBenchPosition?: BenchPosition,
  ): EquipmentCombinations.BENCH_HUB | EquipmentCombinations.BENCH | undefined {
    if(!currentBenchPosition) {
      throw new Error('No bench position configured for a bench exercise');
    }
    if(!previousBenchPosition) {
      return EquipmentCombinations.BENCH_HUB;
    }
    const currentBenchHub = benchBenchPositionsToHubConfig[currentBenchPosition];
    const previousBenchHub = benchBenchPositionsToHubConfig[previousBenchPosition];
    if(currentBenchHub !== previousBenchHub) {
      return EquipmentCombinations.BENCH_HUB;
    }
    if(currentBenchPosition !== previousBenchPosition) {
      return EquipmentCombinations.BENCH;
    }
    return undefined;
  }

  public static getEquipmentRequiringInstructionInfo(equipment: Equipment[]):
    EquipmentRequiringInstruction | undefined {
    const equipmentList: Equipment[] = equipment.filter((equipmentPiece) => {
      const indexOfEquipmentPieceRequiringInstruction =
        equipmentRequiringInstruction.findIndex(
          (equipmentWithInstruction) =>
            equipmentWithInstruction === equipmentPiece,
        );
      const requiresInstruction =
        indexOfEquipmentPieceRequiringInstruction > -1;
      return requiresInstruction;
    });
    const equipmentWithBandsAtEndOfList = equipmentList.sort((a, b) =>
      this.isBand(a) ? 1 : -1,
    );
    return equipmentWithBandsAtEndOfList[0] as
      | EquipmentRequiringInstruction
      | undefined;
  }

  public static isBench(equipment: Equipment) {
    return benchTypes.includes(equipment);
  }

  public static isBand(equipment: Equipment) {
    return bandTypes.includes(equipment);
  }

  private static isHomeBench( equipment: Equipment) {
    return equipment === Equipment.HomeBench;
  }

  private static hasEquipmentPiece(equipmentToLookFor: Equipment, equipmentList: Equipment[]) {
    if (equipmentList == null) {
      return false;
    }
    const match = equipmentList.find((eq) => eq === equipmentToLookFor);
    return match !== undefined;
  }

  private static includesEquipmentPieces(equipmentToLookFor: Equipment[], equipmentList: Equipment[]) {
    if (equipmentList == null) {
      return false;
    }
    const match = equipmentList.find((eq) => equipmentToLookFor.indexOf(eq) !== -1);
    return match;
  }
}
